import { isNotDefined } from '@sgme/fp';
import deepEqual from 'fast-deep-equal';
import { memo, useEffect } from 'react';

import { ExecutionPendingOrderStatus } from '@/App/tiles/orders/ExecutionPendingOrderStatus.tsx';
import { useFixingTileId } from '@/App/tiles/orders/FixingOrder/FixingContext.tsx';
import { FixingOrderCreationTile } from '@/App/tiles/orders/FixingOrder/FixingOrderCreationTile/index.tsx';
import FixingOrderEditingTile from '@/App/tiles/orders/FixingOrder/FixingOrderEditingTile/index.tsx';
import FixingOrderExecutedTile from '@/App/tiles/orders/FixingOrder/FixingOrderExecutedTile/index.tsx';
import { ErrorTile } from '@/App/tiles/shared/ErrorTile.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { selectFixingTile } from '@/store/orders/fixing/selectors.ts';
import { ordersSlice } from '@/store/orders/slice.ts';
import { tilesSlice } from '@/store/tiles/slice.ts';

export const FixingOrderTile = memo(() => {
  const tileId = useFixingTileId();
  const dispatch = useAppDispatch();
  const userPreferences = useAppSelector((state) => state.userPreferences);

  const { status, mode, errorCode } = useAppSelector((state) => {
    const tile = selectFixingTile(state, tileId);

    if (isNotDefined(tile)) {
      return {
        status: 'creating',
      };
    }

    return {
      status: tile.properties.status.type,
      mode: tile.properties.mode,
      errorCode: tile.properties.status.type === 'failed' ? tile.properties.status.errorMessage : undefined,
    };
  }, deepEqual);

  const tileWidth = useAppSelector((state) => tilesSlice.selectors.selectTileW(state, tileId));

  useEffect(() => {
    if (tileWidth !== 1) {
      dispatch(tilesSlice.actions.resizeTile({ tileId, w: 1 }));
    }
  }, [tileId, tileWidth]);

  if (status === 'pending') {
    return <ExecutionPendingOrderStatus isEditing={mode === 'EDIT'} />;
  }

  if (status === 'failed') {
    const handleReset = () => {
      dispatch(ordersSlice.actions.resetOrder({ tileId, userPreferences }));
    };

    return (
      <ErrorTile
        tileId={tileId}
        onReset={handleReset}
        errorCode={errorCode}
        quoteId={tileId}
        errorDescription={
          errorCode === 'order.timeout' ? 'order.timeout.error.message.description' : 'order.error.message.description'
        }
        errorTitle={errorCode === 'order.timeout' ? 'order.timeout.error.message.title' : 'order.error.message.title'}
      />
    );
  }

  if (mode === 'EDIT') {
    return <FixingOrderEditingTile />;
  }

  if (status === 'creating') {
    return <FixingOrderCreationTile />;
  }

  return <FixingOrderExecutedTile />;
});
